/**
 * Safe math expression evaluator for the MATH Handlebars helper.
 * Supports only: numbers, +, -, *, /, parentheses, and variable names
 * from a whitelist. No eval(), no Function(), no code injection.
 */

type Token =
  | { type: 'number'; value: number }
  | { type: 'ident'; name: string }
  | { type: 'op'; op: '+' | '-' | '*' | '/' }
  | { type: 'lparen' }
  | { type: 'rparen' };

const IDENT_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]*/;
const NUMBER_REGEX = /^\d+(\.\d+)?([eE][+-]?\d+)?/;

function tokenize(expr: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const s = expr.trim();

  while (i < s.length) {
    const rest = s.slice(i);
    const ws = /^\s+/.exec(rest);
    if (ws) {
      i += ws[0].length;
      continue;
    }
    if (rest.startsWith('(')) {
      tokens.push({ type: 'lparen' });
      i += 1;
      continue;
    }
    if (rest.startsWith(')')) {
      tokens.push({ type: 'rparen' });
      i += 1;
      continue;
    }
    if (rest.startsWith('+')) {
      tokens.push({ type: 'op', op: '+' });
      i += 1;
      continue;
    }
    if (rest.startsWith('*')) {
      tokens.push({ type: 'op', op: '*' });
      i += 1;
      continue;
    }
    if (rest.startsWith('/')) {
      tokens.push({ type: 'op', op: '/' });
      i += 1;
      continue;
    }
    if (rest.startsWith('-')) {
      tokens.push({ type: 'op', op: '-' });
      i += 1;
      continue;
    }
    const numMatch = NUMBER_REGEX.exec(rest);
    if (numMatch) {
      tokens.push({ type: 'number', value: Number(numMatch[0]) });
      i += numMatch[0].length;
      continue;
    }
    const identMatch = IDENT_REGEX.exec(rest);
    if (identMatch) {
      tokens.push({ type: 'ident', name: identMatch[0] });
      i += identMatch[0].length;
      continue;
    }
    throw new Error(`Unexpected character at position ${i}`);
  }

  return tokens;
}

/**
 * Evaluates a simple math expression string with the given variables.
 * Only allows numbers, + - * /, parentheses, and variable names present in variables.
 * @throws on syntax error, unknown variable, or division by zero
 */
export function evaluateMathExpression(
  expression: string,
  variables: Record<string, number>
): number {
  const tokens = tokenize(expression);
  let pos = 0;

  function peek(): Token | undefined {
    return tokens[pos];
  }

  function consume(): Token | undefined {
    return tokens[pos++];
  }

  function parseExpression(): number {
    let left = parseTerm(false);
    let t = peek();
    while (t?.type === 'op' && (t.op === '+' || t.op === '-')) {
      consume();
      const right = parseTerm(true);
      if (t.op === '+') left = left + right;
      else left = left - right;
      t = peek();
    }
    return left;
  }

  function parseTerm(afterPlusOrMinus: boolean): number {
    let left = parseFactor(afterPlusOrMinus);
    let t = peek();
    while (t?.type === 'op' && (t.op === '*' || t.op === '/')) {
      consume();
      const right = parseFactor(false);
      if (t.op === '*') left = left * right;
      else {
        if (right === 0) throw new Error('Division by zero');
        left = left / right;
      }
      t = peek();
    }
    return left;
  }

  function parseFactor(afterPlusOrMinus: boolean): number {
    const unary = peek();
    if (unary?.type === 'op' && (unary.op === '-' || unary.op === '+')) {
      if (afterPlusOrMinus && unary.op === '+') {
        throw new Error('Invalid expression syntax');
      }
      consume();
      const factor = parseFactor(false);
      return unary.op === '-' ? -factor : factor;
    }

    const t = consume();
    if (!t) throw new Error('Unexpected end of expression');

    if (t.type === 'number') return t.value;

    if (t.type === 'ident') {
      // biome-ignore lint/suspicious/noPrototypeBuiltins: migrated from develop
      if (!Object.prototype.hasOwnProperty.call(variables, t.name)) {
        throw new Error(`Unknown variable: ${t.name}`);
      }
      const value = variables[t.name];
      if (typeof value !== 'number') {
        throw new Error(`Variable ${t.name} is not defined as a number`);
      }

      return value;
    }

    if (t.type === 'lparen') {
      const val = parseExpression();
      const next = consume();
      if (next?.type !== 'rparen') {
        throw new Error('Missing closing parenthesis');
      }
      return val;
    }

    throw new Error(`Unexpected token at position ${pos - 1}`);
  }

  const result = parseExpression();
  if (pos < tokens.length) {
    throw new Error('Unexpected token after expression');
  }
  return result;
}
